import { useState, useEffect } from "react";
import { useI18n } from "@/i18n";
import type { Category } from "@/domain/types";
import { colors, typography, transitions } from "@/design-system/tokens";

interface FieldErrors {
  code?: string;
  name?: string;
}

export function CategoryFormModal({
  item,
  onSave,
  onClose,
}: {
  item: Category | null;
  onSave: (data: { code: string; name: string; description?: string }) => void;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const isEditing = !!item;
  const [code, setCode] = useState(item?.code ?? "");
  const [name, setName] = useState(item?.name ?? "");
  const [description, setDescription] = useState(item?.description ?? "");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const isFormValid = isEditing
    ? !!name.trim()
    : !!code.trim() && !!name.trim();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors: FieldErrors = {};
    if (!isEditing && !code.trim()) errors.code = t.category.errorFieldRequired;
    if (!name.trim()) errors.name = t.category.errorFieldRequired;

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    onSave({ code: code.trim(), name: name.trim(), description: description.trim() || undefined });
  };

  return (
    <ModalOverlay onClose={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: "20px", padding: "32px", width: "100%", maxWidth: "460px", display: "flex", flexDirection: "column", gap: "20px" }}>
        <h2 style={{ fontFamily: typography.fontFamily, fontSize: typography.size.lg, fontWeight: typography.weight.bold, color: "#111", margin: 0 }}>
          {isEditing ? t.category.editCategory : t.category.addCategory}
        </h2>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <Field label={t.category.codeLabel} value={code} onChange={setCode} placeholder={t.category.codePlaceholder} disabled={isEditing} error={fieldErrors.code} required={!isEditing} />
          <Field label={t.category.nameLabel} value={name} onChange={setName} placeholder={t.category.namePlaceholder} error={fieldErrors.name} required />
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <label style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#333" }}>{t.category.descriptionLabel}</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder={t.category.descriptionPlaceholder} rows={3} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#111", background: "#fff", border: "1.5px solid #e5e5e5", borderRadius: "10px", padding: "10px 12px", outline: "none", width: "100%", boxSizing: "border-box", resize: "vertical" }} />
          </div>
          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "4px" }}>
            <button type="button" onClick={onClose} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.medium, color: "#666", background: "transparent", border: "1px solid #e0e0e0", borderRadius: "9px", padding: "10px 20px", cursor: "pointer" }}>{t.category.cancel}</button>
            <button type="submit" disabled={!isFormValid} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#fff", background: isFormValid ? "#111" : "#aaa", border: "none", borderRadius: "9px", padding: "10px 20px", cursor: isFormValid ? "pointer" : "not-allowed", transition: `background ${transitions.fast}`, opacity: isFormValid ? 1 : 0.6 }} onMouseEnter={e => { if (isFormValid) (e.currentTarget as HTMLButtonElement).style.background = "#333"; }} onMouseLeave={e => { if (isFormValid) (e.currentTarget as HTMLButtonElement).style.background = "#111"; }}>{t.category.save}</button>
          </div>
        </form>
      </div>
    </ModalOverlay>
  );
}

function ModalOverlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: "24px" }}>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, disabled = false, error, required = false }: { label: string; value: string; onChange: (v: string) => void; placeholder: string; disabled?: boolean; error?: string; required?: boolean }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      <label style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#333" }}>{label} {required && <span style={{ color: colors.error }}>*</span>}</label>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} disabled={disabled} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#111", background: disabled ? "#f5f5f5" : "#fff", border: `1.5px solid ${error ? colors.error : "#e5e5e5"}`, borderRadius: "10px", padding: "10px 12px", outline: "none", width: "100%", boxSizing: "border-box", opacity: disabled ? 0.6 : 1 }} />
      {error && <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: colors.error }}>{error}</span>}
    </div>
  );
}
